import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Download,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
} from 'lucide-react';
import api from '../../utils/api';

// Shared voice primitives — the pieces every voice surface needs and none of
// them should own: text normalisation, the audio player hook, the waveform
// strip, the status dots and the confidence-underlined raw view.
//
// Extracted from VoiceTab.jsx on 2026-09-23, when the Notes and Echoes lists
// merged into one. Before that, EchoCard imported AudioStrip *from VoiceTab*
// while VoiceTab imported EchoCard — a cycle that happened to work and would
// not have survived a third card. One owner, no cycle.

const SAGE = '#7C9A82';
const AMBER = '#D4A574';
const STONE = '#A8A29E';
const WAVE_BAR_COUNT = 48;

export const normalizeForCompare = (s) =>
  (s || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[^\w\s]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export function formatSeconds(totalSec) {
  const t = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function computePeaks(channelData, barCount) {
  const blockSize = Math.max(1, Math.floor(channelData.length / barCount));
  const peaks = new Array(barCount).fill(0);
  for (let i = 0; i < barCount; i++) {
    let max = 0;
    const start = i * blockSize;
    const end = Math.min(channelData.length, start + blockSize);
    for (let j = start; j < end; j++) {
      const a = Math.abs(channelData[j]);
      if (a > max) max = a;
    }
    peaks[i] = max;
  }
  const globalMax = peaks.reduce((m, v) => (v > m ? v : m), 0) || 1;
  return peaks.map((p) => p / globalMax);
}

// Stable decorative wave used before the audio is decoded; varies per id so
// adjacent cards don't look identical.
export function staticPeaks(seed) {
  const arr = new Array(WAVE_BAR_COUNT);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  for (let i = 0; i < WAVE_BAR_COUNT; i++) {
    h = (h * 1103515245 + 12345) | 0;
    arr[i] = 0.25 + (((h >>> 8) & 0xff) / 255) * 0.55;
  }
  return arr;
}

// ---- audio playback hook ---------------------------------------------

export function useAudioPlayer({ noteId, fallbackDurationMs }) {
  const [phase, setPhase] = useState('idle'); // idle | loading | playing | paused | error
  const [progress, setProgress] = useState(0);
  const fallbackDuration = (fallbackDurationMs || 0) / 1000;
  const [duration, setDuration] = useState(fallbackDuration);
  const [waveform, setWaveform] = useState(null);

  const blobUrlRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(
    () => () => {
      const a = audioRef.current;
      if (a) {
        try { a.pause(); } catch { /* ignore */ }
        a.src = '';
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    },
    []
  );

  const loadAndPlay = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await api.get(`/api/voice/${noteId}/audio`, { responseType: 'blob' });
      blobUrlRef.current = URL.createObjectURL(res.data);

      try {
        const ab = await res.data.arrayBuffer();
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        const buf = await ctx.decodeAudioData(ab);
        setWaveform(computePeaks(buf.getChannelData(0), WAVE_BAR_COUNT));
        if (Number.isFinite(buf.duration) && buf.duration > 0) setDuration(buf.duration);
      } catch {
        // Decode failed — keep the decorative wave.
      }

      const audio = new Audio(blobUrlRef.current);
      audio.preload = 'auto';
      audio.addEventListener('play', () => setPhase('playing'));
      audio.addEventListener('pause', () => {
        if (!audio.ended) setPhase('paused');
      });
      audio.addEventListener('ended', () => {
        setPhase('idle');
        setProgress(0);
      });
      audio.addEventListener('timeupdate', () => {
        const d =
          Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : fallbackDuration || 1;
        const next = Math.min(1, audio.currentTime / d);
        setProgress(Number.isFinite(next) ? next : 0);
      });
      audio.addEventListener('error', () => setPhase('error'));
      audioRef.current = audio;
      await audio.play();
    } catch (err) {
      console.warn('audio load/play failed:', err?.message);
      setPhase('error');
    }
  }, [noteId, fallbackDuration]);

  const togglePlay = useCallback(() => {
    if (phase === 'idle' || phase === 'error') {
      loadAndPlay();
    } else if (phase === 'playing') {
      audioRef.current?.pause();
    } else if (phase === 'paused') {
      audioRef.current?.play().catch(() => setPhase('error'));
    }
  }, [phase, loadAndPlay]);

  const pause = useCallback(() => {
    if (audioRef.current && phase === 'playing') {
      try { audioRef.current.pause(); } catch { /* ignore */ }
    }
  }, [phase]);

  return { phase, progress, duration, waveform, togglePlay, pause };
}

// ---- atoms ------------------------------------------------------------

// Tiny dot rendered next to the status pill while a voice note is not yet in
// Chat memory. Tells the user (in plain UI language, no model jargon) whether
// Chat will actually see this note. Renders nothing for terminal states
// (complete / skipped / null).
//
// Pass `effective_embedding_status` when the API provides it — it separates
// "queued, back shortly" from "blocked, and only you can unblock it", which the
// raw embedding_status cannot express (both are 'pending'). Falls back to
// embedding_status, so a response predating that field still renders correctly.
export function PendingEmbedDot({ embeddingStatus }) {
  const map = {
    pending: {
      tooltip: 'Not yet used in Chat memory — embedding will generate shortly.',
      label: 'Embedding pending',
      color: AMBER,
      pulse: true,
    },
    processing: {
      tooltip: 'Currently being added to Chat memory…',
      label: 'Embedding pending',
      color: AMBER,
      pulse: true,
    },
    failed: {
      tooltip: "Couldn't add to Chat memory. Save again to retry.",
      label: 'Embedding failed',
      color: AMBER,
      pulse: false,
    },
    // Waiting on the user, not on us. Muted and still, because a pulse promises
    // progress that will never come on its own — nothing moves until they turn
    // AI on in Profile.
    ai_consent_pending: {
      tooltip: 'AI is off, so this stays out of Chat memory. Turn it on in Profile to include it.',
      label: 'AI paused — not in Chat memory',
      color: STONE,
      pulse: false,
    },
  };
  const m = map[embeddingStatus];
  if (!m) return null;
  return (
    <span
      role="img"
      aria-label={m.label}
      title={m.tooltip}
      className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
      style={{
        backgroundColor: m.color,
        boxShadow: m.pulse ? `0 0 0 0 ${m.color}` : undefined,
        animation: m.pulse ? 'voicePendingPulse 1.6s ease-out infinite' : undefined,
      }}
    />
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending:         { label: 'Pending',       color: AMBER,     bg: `${AMBER}1A`, icon: null },
    transcribed:     { label: 'Almost ready',  color: AMBER,     bg: `${AMBER}1A`, icon: null },
    cleaned:         { label: 'Cleaning',      color: AMBER,     bg: `${AMBER}1A`, icon: null },
    awaiting_review: { label: 'Review',        color: SAGE,      bg: `${SAGE}1A`,  icon: null },
    accepted:        { label: 'Saved',         color: '#4a6850', bg: `${SAGE}22`,  icon: Check },
    empty_speech:    { label: 'Empty',         color: '#78716c', bg: '#f5f5f4',    icon: null },
    failed:          { label: 'Failed',        color: '#92400e', bg: `${AMBER}22`, icon: null },
  };
  const m = map[status] || { label: status || '—', color: '#78716c', bg: '#f5f5f4', icon: null };
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shrink-0"
      style={{ backgroundColor: m.bg, color: m.color }}
    >
      {Icon && <Icon size={13} strokeWidth={2.5} />}
      {m.label}
    </span>
  );
}

// Right-side control cluster for the audio strip. The badge slot is dynamic:
//   • awaiting_review OR (accepted + unsaved changes) → "Save to journal"
//   • everything else → read-only status pill
// Delete sits next to it as a small icon. This collapses what used to be three
// separate UI elements (top-right delete X, top-right Saved pill, bottom-right
// Save to journal button) into one coherent control.
export function ActionPill({ icon: Icon, label, onClick, disabled, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-stone-700 dark:text-stone-200 bg-white dark:bg-stone-800/60 border border-stone-200 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : Icon ? <Icon size={14} /> : null}
      {label}
    </button>
  );
}

// ---- audio overflow menu ----------------------------------------------

export const MIME_TO_EXT = {
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'm4a',
  'audio/webm': 'webm',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/mpeg': 'mp3',
};

export function AudioOverflowMenu({ note }) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMounted(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setErrorMsg(null);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setErrorMsg(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const buildFilename = useCallback(() => {
    const ext = MIME_TO_EXT[(note?.mime_type || '').toLowerCase()] || 'audio';
    const d = note?.created_at ? new Date(note.created_at) : new Date();
    const dateStr = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(d);
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const h12 = ((hours + 11) % 12) + 1;
    const timeStr = `${h12}.${minutes}${ampm}`;
    return `Mindwire · ${dateStr} · ${timeStr}.${ext}`;
  }, [note?.mime_type, note?.created_at]);

  const handleSave = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setErrorMsg(null);
    const name = buildFilename();
    try {
      const { data: blob } = await api.get(
        `/api/voice/${note.id}/audio/download?name=${encodeURIComponent(name)}`,
        { responseType: 'blob' }
      );

      // Mobile: native share sheet (Files, AirDrop, Voice Memos…).
      const file =
        typeof File !== 'undefined'
          ? new File([blob], name, { type: blob.type || 'audio/mp4' })
          : null;
      if (
        file &&
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === 'function'
      ) {
        try {
          await navigator.share({ files: [file], title: name });
          setSavedTick(true);
          setTimeout(() => {
            setSavedTick(false);
            setOpen(false);
          }, 1200);
          return;
        } catch (shareErr) {
          // AbortError = user dismissed; treat as no-op.
          if (shareErr?.name === 'AbortError') {
            return;
          }
          // fall through to anchor download
        }
      }

      // Desktop / fallback: direct save via blob URL.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setSavedTick(true);
      setTimeout(() => {
        setSavedTick(false);
        setOpen(false);
      }, 1200);
    } catch (err) {
      console.warn('voice download failed:', err?.message);
      setErrorMsg("Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  }, [busy, buildFilename, note?.id]);

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-all"
      >
        <MoreHorizontal size={16} />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 top-full mt-2 w-60 rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 shadow-lg p-1.5 z-20 origin-top-right transition-all duration-200 ease-out ${
            mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleSave}
            disabled={busy}
            className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl hover:bg-stone-50 dark:hover:bg-stone-700/60 transition-colors text-left disabled:opacity-60"
          >
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-full shrink-0"
              style={{ backgroundColor: `${SAGE}22`, color: SAGE }}
            >
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : savedTick ? (
                <Check size={16} />
              ) : (
                <Download size={16} />
              )}
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-stone-700 dark:text-stone-200">
                {savedTick ? 'Saved' : 'Save audio file'}
              </span>
              <span className="text-xs text-stone-400 dark:text-stone-500">
                {savedTick ? 'In your files' : 'Download to your device'}
              </span>
            </span>
          </button>
          {errorMsg && (
            <p className="px-3.5 pb-2 pt-1 text-xs text-rose-500">{errorMsg}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- audio strip ------------------------------------------------------

export function AudioStrip({ note }) {
  const { phase, progress, duration, waveform, togglePlay } = useAudioPlayer({
    noteId: note.id,
    fallbackDurationMs: note.duration_ms,
  });

  const isPlaying = phase === 'playing';
  const isPaused = phase === 'paused';
  const isLoading = phase === 'loading';
  const displayDuration = duration || (note.duration_ms || 0) / 1000;
  const playedSec = displayDuration * progress;
  const peaks = waveform || staticPeaks(note.id);
  const playedIdx = Math.floor(progress * peaks.length);

  // Map each waveform bar to its Whisper segment by midpoint time, then mark
  // it low-confidence if that segment is in the flagged set. Visible even
  // before playback (the decorative static peaks still map by even time
  // slices because Whisper's segment timestamps are absolute seconds).
  const lowConfBarSet = useMemo(() => {
    const segments = Array.isArray(note?.segments) ? note.segments : [];
    const set = new Set();
    if (!segments.length || !displayDuration || displayDuration <= 0) return set;
    const lowIds = new Set();
    for (const item of note?.low_confidence_segments || []) {
      if (Number.isInteger(item?.idx)) lowIds.add(item.idx);
    }
    if (lowIds.size === 0) return set;
    const barCount = peaks.length;
    for (let i = 0; i < barCount; i++) {
      const barTime = ((i + 0.5) / barCount) * displayDuration;
      for (let si = 0; si < segments.length; si++) {
        const s = segments[si];
        if (
          typeof s?.start === 'number' &&
          typeof s?.end === 'number' &&
          barTime >= s.start &&
          barTime <= s.end
        ) {
          if (lowIds.has(si)) set.add(i);
          break;
        }
      }
    }
    return set;
  }, [note?.segments, note?.low_confidence_segments, displayDuration, peaks.length]);

  return (
    <div className="flex items-center gap-3 sm:gap-4">
      <button
        type="button"
        onClick={togglePlay}
        disabled={isLoading}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-stone-900 text-white shadow-sm transition-transform active:scale-95 disabled:opacity-70"
      >
        {isLoading ? (
          <Loader2 size={20} className="animate-spin" />
        ) : isPlaying ? (
          <Pause size={20} fill="currentColor" />
        ) : (
          <Play size={20} fill="currentColor" style={{ marginLeft: 2 }} />
        )}
      </button>

      <div className="flex-1 min-w-0 flex items-center gap-3 rounded-full bg-stone-100/70 dark:bg-stone-800/40 px-4 sm:px-5 py-3.5">
        <svg
          viewBox={`0 0 ${WAVE_BAR_COUNT} 24`}
          preserveAspectRatio="none"
          className="flex-1 h-6"
          aria-hidden="true"
        >
          {peaks.map((p, i) => {
            const h = Math.max(2.5, p * 22);
            const y = (24 - h) / 2;
            const isPlayed = i < playedIdx;
            // Amber tint on bars whose corresponding Whisper segment was
            // flagged low-confidence. Played bars stay full opacity, unplayed
            // at 40% — same convention as the sage baseline.
            const isLowConf = lowConfBarSet.has(i);
            const base = isLowConf ? AMBER : SAGE;
            return (
              <rect
                key={i}
                x={i + 0.18}
                y={y}
                width={0.64}
                height={h}
                rx={0.3}
                fill={isPlayed ? base : `${base}66`}
              />
            );
          })}
        </svg>
        <span className="text-sm sm:text-base tabular-nums text-stone-500 dark:text-stone-400 shrink-0 font-medium">
          {isPlaying || isPaused
            ? `${formatSeconds(playedSec)} / ${formatSeconds(displayDuration)}`
            : formatSeconds(displayDuration)}
        </span>
      </div>

      <AudioOverflowMenu note={note} />
    </div>
  );
}

// ---- processing indicator (for pending / cleanup-in-flight) ----------

export function ProcessingIndicator({ label, note }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);
  const startMs = note?.created_at ? new Date(note.created_at).getTime() : now;
  const elapsedMs = Math.max(0, now - startMs);
  const durationMs = Math.max(1000, note?.duration_ms || 10000);
  const estimatedMs = Math.max(3000, durationMs / 5 + 1000);
  const fraction = Math.min(1, elapsedMs / estimatedMs);
  const rawPct = fraction * 75;
  const labelPct = Math.floor(rawPct / 5) * 5;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-stone-400 dark:text-stone-500 italic">{label}</p>
        <p className="text-xs tabular-nums text-stone-400 dark:text-stone-500">{labelPct}%</p>
      </div>
      <div className="relative h-1 rounded-full overflow-hidden bg-stone-100 dark:bg-stone-700/70">
        <div
          className="absolute top-0 left-0 h-full rounded-full transition-[width] duration-200 ease-out"
          style={{ width: `${Math.max(2, rawPct)}%`, backgroundColor: SAGE }}
        />
      </div>
    </div>
  );
}

// ---- find typos -------------------------------------------------------

export function SegmentedRawText({ rawText, segments, lowConfidenceSegments }) {
  const lowConfIds = useMemo(() => {
    const s = new Set();
    for (const item of lowConfidenceSegments || []) {
      if (Number.isInteger(item?.idx)) s.add(item.idx);
    }
    return s;
  }, [lowConfidenceSegments]);

  if (!segments || segments.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-stone-800 dark:text-[#F0EDE8] text-base leading-relaxed whitespace-pre-wrap">
          {rawText}
        </p>
        <p className="text-[11px] text-stone-400 dark:text-stone-500 italic">
          Segment-level confidence data unavailable for this recording — either
          this note pre-dates the feature, or the backend didn't return a
          `segments` field on this fetch. Confidence underlines will appear on
          newer recordings.
        </p>
      </div>
    );
  }

  const hasAnyLowConf = lowConfIds.size > 0;
  // Pure inline styles for the wavy underline so this doesn't depend on
  // Tailwind 3.3+ (decoration-wavy / underline-offset-* shorthands).
  const lowConfStyle = {
    textDecorationLine: 'underline',
    textDecorationStyle: 'wavy',
    textDecorationColor: AMBER,
    textUnderlineOffset: '4px',
  };
  const legendStyle = {
    textDecorationLine: 'underline',
    textDecorationStyle: 'wavy',
    textDecorationColor: AMBER,
    textUnderlineOffset: '2px',
  };

  return (
    <div className="space-y-2">
      <p className="text-stone-800 dark:text-[#F0EDE8] text-base leading-relaxed whitespace-pre-wrap">
        {segments.map((seg, i) => {
          const isLowConf = lowConfIds.has(i);
          const txt = seg?.text ?? '';
          return (
            <span
              key={i}
              style={isLowConf ? lowConfStyle : undefined}
              title={
                isLowConf
                  ? 'The transcription model was unsure about this part. Tap Edit on the Cleaned tab to fix.'
                  : undefined
              }
            >
              {txt}
            </span>
          );
        })}
      </p>
      {hasAnyLowConf ? (
        <p className="text-[11px] text-stone-500 dark:text-stone-400 leading-relaxed">
          {lowConfIds.size} of {segments.length} parts flagged for review —
          look at the <span style={legendStyle}>wavy-underlined</span> words and
          fix on the Cleaned tab if any are wrong.
        </p>
      ) : (
        <p
          className="inline-flex items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400"
          aria-label="Transcription confidence: all clear"
        >
          <span
            className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full"
            style={{ backgroundColor: `${SAGE}22`, color: SAGE }}
          >
            <Check size={9} strokeWidth={3} />
          </span>
          All clear — every part of this recording was transcribed confidently.
        </p>
      )}
    </div>
  );
}

// ---- tabs -------------------------------------------------------------

